/**
 * Avatar image processing utilities.
 * Handles resizing, compression, and size-capping for player avatars.
 */

// Target thumbnail dimensions (square)
export const AVATAR_SIZE = 96;

// Maximum allowed payload size in bytes (50KB)
export const MAX_AVATAR_BYTES = 50 * 1024;

// Initial JPEG quality
const INITIAL_QUALITY = 0.7;

// Quality reduction step when size exceeds cap
const QUALITY_STEP = 0.1;

// Minimum quality before giving up
const MIN_QUALITY = 0.3;

/**
 * Load an image from a File object.
 * @param {File} file - The image file to load
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
function loadImage(file) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));

        const reader = new FileReader();
        reader.onload = (e) => {
            img.src = e.target.result;
        };
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
    });
}

/**
 * Resize and crop image to a square thumbnail.
 * Centers the crop on the image.
 * @param {HTMLImageElement} img - Source image
 * @param {number} size - Target size (width and height)
 * @param {number} quality - JPEG quality (0-1)
 * @returns {string} - Data URL of the resized image
 */
function resizeToSquare(img, size, quality) {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    // Calculate crop dimensions (center crop to square)
    const minDim = Math.min(img.width, img.height);
    const sx = (img.width - minDim) / 2;
    const sy = (img.height - minDim) / 2;

    // Draw cropped and scaled image
    ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);

    // Export as JPEG with specified quality
    return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Calculate the byte size of a data URL.
 * @param {string} dataUrl - The data URL string
 * @returns {number} - Approximate size in bytes
 */
function getDataUrlSize(dataUrl) {
    // Remove the data URL prefix to get just the base64 part
    const base64 = dataUrl.split(",")[1] || "";
    // Base64 encodes 3 bytes as 4 characters
    return Math.ceil((base64.length * 3) / 4);
}

/**
 * Process an image file into a compressed avatar thumbnail.
 * Resizes to AVATAR_SIZE x AVATAR_SIZE and compresses to fit within MAX_AVATAR_BYTES.
 *
 * @param {File} file - The image file to process
 * @returns {Promise<string|null>} - Data URL of the processed avatar, or null if processing fails
 */
export async function processAvatarImage(file) {
    try {
        // Validate file type
        if (!file.type.startsWith("image/")) {
            console.warn("Avatar: Invalid file type", file.type);
            return null;
        }

        // Load the image
        const img = await loadImage(file);

        // Try with initial quality, reduce if needed
        let quality = INITIAL_QUALITY;
        let dataUrl = resizeToSquare(img, AVATAR_SIZE, quality);
        let size = getDataUrlSize(dataUrl);

        // Iteratively reduce quality until under size cap
        while (size > MAX_AVATAR_BYTES && quality > MIN_QUALITY) {
            quality -= QUALITY_STEP;
            dataUrl = resizeToSquare(img, AVATAR_SIZE, quality);
            size = getDataUrlSize(dataUrl);
        }

        // If still too large, give up (shouldn't happen at 96x96)
        if (size > MAX_AVATAR_BYTES) {
            console.warn("Avatar: Could not compress below size cap", { size, quality });
            return null;
        }

        console.log("Avatar: Processed successfully", {
            originalSize: file.size,
            finalSize: size,
            quality: quality.toFixed(2),
            dimensions: `${AVATAR_SIZE}x${AVATAR_SIZE}`
        });

        return dataUrl;
    } catch (error) {
        console.warn("Avatar: Processing failed", error);
        return null;
    }
}

/**
 * Generate a consistent color for a player based on their nickname.
 * Uses theme-compatible colors from a curated palette.
 * @param {string} nickname - The player's nickname
 * @returns {string} - Hex color code
 */
export function getAvatarColor(nickname) {
    const colors = [
        "#6366f1", // indigo
        "#8b5cf6", // violet
        "#ec4899", // pink
        "#f43f5e", // rose
        "#f97316", // orange
        "#eab308", // yellow
        "#22c55e", // green
        "#14b8a6", // teal
        "#06b6d4", // cyan
        "#3b82f6", // blue
    ];

    // Simple hash based on nickname
    let hash = 0;
    const str = nickname || "?";
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
}

/**
 * Generate initials from a name for placeholder avatars.
 * @param {string} name - The player's name
 * @returns {string} - One or two character initials
 */
export function getInitials(name) {
    if (!name || typeof name !== "string") return "?";

    const trimmed = name.trim();
    if (!trimmed) return "?";

    // Split by spaces and get first letter of first two words
    const words = trimmed.split(/\s+/).filter(w => w.length > 0);
    if (words.length === 0) return "?";

    if (words.length === 1) {
        return words[0].charAt(0).toUpperCase();
    }

    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
}
